import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';

import { ExistsFunction, TFunction } from 'i18next';
import { AxiosResponse } from 'axios';
import Form, { FormTemplate, FormUtils, InputType } from '../../../molecules/Form';
import GenericFormTemplate from './GenericFormTemplate';
import { InteractionStatus } from '../../../../types/InteractionStatus';
import { setPartOfStringBold, translatePartOfStringWithLink } from '../../../../lib/StringUtils/StringHTMLUtils';
import withAppContext from '../../../hocs/withAppContext/withAppContext';
import { optionsProcessor } from '../../../molecules/Form/components/FormSelect/FormSelect';
import { isEmptyObject } from '../../../../lib/typeGuards';
import { PortalConfiguration } from '../../../../types/api/PortalConfiguration';
import IForm from '../../../../types/IForm';
import { IParameterProperty } from '../../../../types/IParameter';
import { SonApiResponse } from '../../../../types/api/SonApiResponse';
import { IFormModel } from '../../../molecules/Form/models/FormModel';

type GenericFormProps = {
  form: IForm;
  checkPageExists?: boolean;
  translationBaseKey: string;
  hideHeadline: boolean;
  hideFormHelpPhrase?: boolean;
  portalConfiguration: PortalConfiguration;
  additions?: Record<string, any>;
  onChange?: () => void;
  id: string;
  i18n: {
    exists: ExistsFunction;
  };
  t: TFunction;
};

type Message = { id: string; text: any; status: InteractionStatus };

class GenericForm extends Component<GenericFormProps> {
  static defaultProps = {
    hideFormHelpPhrase: false,
    additions: {},
    checkPageExists: false,
  };

  private orderOfInputsByName: { [key: string]: number };

  constructor(props: GenericFormProps) {
    super(props);

    this.onSubmit = this.onSubmit.bind(this);
    this.orderOfInputsByName = { title: 0, leadText: 1 };
  }

  getStatusMessages() {
    const statusMessages: Record<string, Message | null> = {};

    if (this.props.i18n.exists(`${this.props.translationBaseKey}.status.success`)) {
      statusMessages[InteractionStatus.SUCCESS] = FormUtils.createStatusMessage(
        setPartOfStringBold(`${this.props.translationBaseKey}.status.success`, this.props.i18n, this.props.t)
      );
    }

    if (this.props.i18n.exists(`${this.props.translationBaseKey}.status.sending`)) {
      statusMessages[InteractionStatus.SENDING] = FormUtils.createStatusMessage(
        setPartOfStringBold(`${this.props.translationBaseKey}.status.sending`, this.props.i18n, this.props.t)
      );
    }

    if (this.props.i18n.exists(`${this.props.translationBaseKey}.status.fail`)) {
      statusMessages[InteractionStatus.FAIL] = FormUtils.createStatusMessage(
        translatePartOfStringWithLink(
          `${this.props.translationBaseKey}.status.fail`,
          this.props.portalConfiguration.son_portal_support_email &&
            `mailto:${this.props.portalConfiguration.son_portal_support_email}`
        )
      );
    }

    return isEmptyObject(statusMessages) ? null : statusMessages;
  }

  reviseStatusMessagesOnResponse(
    messages: Record<string, Message | null> | null,
    response: AxiosResponse<SonApiResponse, any> | undefined
  ) {
    if (response && messages) {
      if (response.data && response.data.url) {
        messages[InteractionStatus.SUCCESS] = FormUtils.createStatusMessage(
          translatePartOfStringWithLink(`${this.props.translationBaseKey}.status.success`, response.data.url)
        );
      }

      if (response.status >= 300 && response.status !== 400 && response.status !== 500 && response.status !== 403) {
        messages[InteractionStatus.FAIL] = FormUtils.createStatusMessage(
          this.props.t(`${this.props.translationBaseKey}.status.notExists`)
        );
      }
    }

    return messages;
  }

  setDefaultValue(param: IParameterProperty) {
    if (param && this.props.i18n.exists(`${this.props.translationBaseKey}.inputs.${param.name}.defaultValue`)) {
      param.defaultValue =
        this.props.t(`${this.props.translationBaseKey}.inputs.${param.name}.defaultValue`) || undefined;
    }
  }

  normalizeParameters(parameters: IParameterProperty[]) {
    const tmp = [...parameters];
    tmp
      .sort((el1, el2) => this.orderOfInputsByName[el1.name] - this.orderOfInputsByName[el2.name])
      .forEach(param => {
        this.setDefaultValue(param);

        if (param.type === InputType.SELECT) {
          param.options = optionsProcessor(param.name, param.options as any, this.props.translationBaseKey);
        }
      });

    return tmp;
  }

  onSubmit(event: Event, model: IFormModel & { submit: () => Promise<any> }) {
    if (event) {
      event.preventDefault();
    }

    model.submit().then(() => {
      if (this.props.onChange) {
        this.props.onChange();
      }
    });
  }

  render() {
    return (
      <Form
        id={this.props.id}
        checkPageExists={this.props.checkPageExists}
        onSubmit={this.onSubmit}
        model={{
          url: this.props.form.url,
          params: this.normalizeParameters(this.props.form.params || []),
          method: this.props.form.method,
        }}
        submittableWithoutChange={
          this.props.form.params?.length === 1 && this.props.form.params[0]?.options?.length === 1
        }
      >
        <FormTemplate>
          {model => (
            <GenericFormTemplate
              {...model}
              hideHeadline={this.props.hideHeadline}
              hideFormHelpPhrase={this.props.hideFormHelpPhrase}
              // @ts-expect-error -- form typing sucks... especially generic form
              statusMessages={this.reviseStatusMessagesOnResponse(this.getStatusMessages(), model.response)}
              translationBaseKey={this.props.translationBaseKey}
              i18n={this.props.i18n}
              t={this.props.t}
              additions={this.props.additions}
            />
          )}
        </FormTemplate>
      </Form>
    );
  }
}

export default withTranslation()(withAppContext(GenericForm));
